import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import {
  Alert,
  Button,
  ActivityIndicator,
  LightBox,
  LightBoxInstance,
  Tooltip,
} from '@kontentino/ui';
import { HyperText } from '@kontentino/kontentino-post-previews';
import type { TranslationJobResponse } from 'types/AiHubTranslation';
import MediaPreview from 'app/components/compoundMediaPreview';
import { useLanguageLabel } from './LanguageSelectorPopover';
import Modal from 'components/shared/Modal';
import AiImageSuggestionsSettings from 'app/modules/aiContent/components/AiImageSuggestionsSettings';
import TextEditorAiContentProvider from 'app/modules/textEditor/components/textEditorAiContent/TextEditorAiContentProvider';
import useSubscriptionInfo from 'app/hooks/useSubscriptionInfo';
import { useQueryClient } from 'react-query';
import { queryKey } from 'constants/queryKey';
import { useToast } from 'app/hooks/useToast';
import TranslationApi from './api/translationApi';

type Tab = 'original' | 'translated';

type Props = {
  postId: number;
  translationJob: TranslationJobResponse;
  onApply: (field: 'postText' | 'firstComment' | 'images') => void;
  onRevert: (field: 'postText' | 'firstComment' | 'images') => void;
  onApplyAll: () => void;
  onRevertAll: () => void;
  appliedFields: {
    postText: boolean;
    firstComment: boolean;
    images: boolean;
  };
  isApplying: boolean;
  applyingFields?: {
    postText: boolean;
    firstComment: boolean;
    images: boolean;
    all: boolean;
  };
  isLocked?: boolean;
  retranslateSlot?: React.ReactNode;
  onRetryFailedImages?: () => void;
  isRetryingImages?: boolean;
  canEditText?: boolean;
  canEditAssets?: boolean;
  canTriggerTranslation?: boolean;
  pageType?: number;
  page?: { id: number; type: number };
};

const TranslationResults: FC<Props> = ({
  postId,
  translationJob,
  onApply,
  onRevert,
  onApplyAll,
  onRevertAll,
  appliedFields,
  isApplying,
  applyingFields,
  isLocked = false,
  retranslateSlot,
  onRetryFailedImages,
  isRetryingImages = false,
  canEditText = true,
  canEditAssets = true,
  canTriggerTranslation = true,
  pageType,
  page,
}) => {
  const { t } = useTranslation();
  const { accountPlanType } = useSubscriptionInfo();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [editingImageUrl, setEditingImageUrl] = useState<string | null>(null);
  const [editingCardIndex, setEditingCardIndex] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('translated');
  const lightbox = useRef<LightBoxInstance>();
  const sourceLanguageLabel = useLanguageLabel(
    translationJob.sourceLanguage ?? null,
  );
  const targetLanguageLabel = useLanguageLabel(
    translationJob.targetLanguage ?? null,
  );

  const { fields, status } = translationJob;

  // Show loading skeleton while processing
  if (status === 'pending' || status === 'processing') {
    return (
      <div className="tw-rounded-lg tw-border tw-border-grayscale-20 tw-bg-grayscale-5 tw-p-4">
        {/* Info alert about processing time */}
        <Alert variant="info" className="tw-mb-4">
          <div className="tw-flex tw-items-center tw-gap-2">
            <ActivityIndicator />
            <span>
              {t('translation.processing', {
                defaultValue:
                  'Translating content... This may take up to 1 minute.',
              })}
            </span>
          </div>
        </Alert>

        {/* Skeleton for post text */}
        {fields?.postText && (
          <div className="tw-mb-4">
            <Skeleton height={20} width="30%" className="tw-mb-2" />
            <Skeleton count={3} />
          </div>
        )}

        {/* Skeleton for first comment */}
        {fields?.firstComment && (
          <div className="tw-mb-4">
            <Skeleton height={20} width="30%" className="tw-mb-2" />
            <Skeleton count={2} />
          </div>
        )}

        {/* Skeleton for images */}
        {fields?.images && fields.images.length > 0 && (
          <div>
            <Skeleton height={20} width="20%" className="tw-mb-2" />
            <div className="tw-flex tw-gap-2">
              {fields.images.map((_, index) => (
                <Skeleton key={index} height={120} width={120} />
              ))}
            </div>
          </div>
        )}

        {(() => {
          const images = translationJob.fields?.images ?? [];
          const totalImages = images.length;
          const translatedImages = images.filter(
            (img) => img.status === 'completed',
          ).length;

          return totalImages > 0 ? (
            <div className="tw-mt-4 tw-text-sm tw-text-grayscale-140">
              {t('translation.progress', {
                translated: translatedImages,
                total: totalImages,
                defaultValue: 'Translated {{translated}} of {{total}} images',
              })}
            </div>
          ) : null;
        })()}
      </div>
    );
  }

  if (!fields || status !== 'completed') {
    return null;
  }

  const hasPostText = !!fields.postText?.translated;
  const hasFirstComment = !!fields.firstComment?.translated;
  const hasImages = !!fields.images && fields.images.length > 0;
  const hasAnyTranslation = hasPostText || hasFirstComment || hasImages;

  if (!hasAnyTranslation) {
    return null;
  }

  const completedImages = hasImages
    ? fields.images!.filter(
        (img) => img.status === 'completed' && img.translatedUrl,
      )
    : [];

  const failedImages = hasImages
    ? fields.images!.filter((img) => img.status === 'failed')
    : [];

  const galleryItems = [
    ...completedImages.map((img, index) => ({
      src: activeTab === 'original' ? img.originalUrl : img.translatedUrl!,
      thumb: activeTab === 'original' ? img.originalUrl : img.translatedUrl!,
      alt: `${t('translation.image', { defaultValue: 'Image' })} ${index + 1}`,
    })),
    ...failedImages.map((img, index) => ({
      src: img.originalUrl,
      thumb: img.originalUrl,
      alt: `${t('translation.image', { defaultValue: 'Image' })} ${
        completedImages.length + index + 1
      }`,
    })),
  ];

  const hasMultipleEditableFields =
    (canEditText ? (hasPostText ? 1 : 0) + (hasFirstComment ? 1 : 0) : 0) +
      (canEditAssets && hasImages ? 1 : 0) >
    1;

  const canApplyAll =
    hasMultipleEditableFields &&
    ((canEditText && hasPostText && !appliedFields.postText) ||
      (canEditText && hasFirstComment && !appliedFields.firstComment) ||
      (canEditAssets && hasImages && !appliedFields.images));

  const canRevertAll =
    hasMultipleEditableFields &&
    ((canEditText && hasPostText && appliedFields.postText) ||
      (canEditText && hasFirstComment && appliedFields.firstComment) ||
      (canEditAssets && hasImages && appliedFields.images));

  return (
    <div
      className="tw-rounded-lg tw-border tw-border-grayscale-20 tw-bg-white tw-p-4"
      data-cy="translation-results"
    >
      {/* Tab Header */}
      <div className="tw-mb-4 tw-flex tw-items-center tw-justify-between tw-border-b tw-border-grayscale-20">
        <div className="tw-flex tw-gap-4">
          <button
            onClick={() => setActiveTab('original')}
            className={`tw-border-b-2 tw-px-2 tw-py-2 tw-text-sm tw-font-medium ${
              activeTab === 'original'
                ? 'tw-border-primary-100 tw-text-primary-100'
                : 'tw-border-transparent tw-text-grayscale-140 hover:tw-text-grayscale-180'
            }`}
            data-cy="translation-tab-original"
          >
            {t('translation.originalValue', { defaultValue: 'Original' })}
            {translationJob.sourceLanguage && (
              <span className="tw-font-normal tw-ml-1 tw-text-grayscale-100">
                ({sourceLanguageLabel})
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('translated')}
            className={`tw-border-b-2 tw-px-2 tw-py-2 tw-text-sm tw-font-medium ${
              activeTab === 'translated'
                ? 'tw-border-primary-100 tw-text-primary-100'
                : 'tw-border-transparent tw-text-grayscale-140 hover:tw-text-grayscale-180'
            }`}
            data-cy="translation-tab-translated"
          >
            {t('translation.translatedValue', { defaultValue: 'Translated' })}
            {translationJob.targetLanguage && (
              <span className="tw-font-normal tw-ml-1 tw-text-grayscale-100">
                ({targetLanguageLabel})
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Content Sections */}
      <div className="tw-space-y-4">
        {/* Post Text Section */}
        {hasPostText && (
          <div
            className="tw-rounded-lg tw-border tw-border-grayscale-20 tw-p-3"
            data-cy="translation-results-post-text"
          >
            <div className="tw-mb-2 tw-flex tw-items-center tw-justify-between">
              <h4 className="tw-text-sm tw-font-medium tw-text-grayscale-180">
                {t('translation.postTextSection', {
                  defaultValue: 'Post Text',
                })}
              </h4>
              {canEditText &&
                (appliedFields.postText ? (
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <span className="tw-text-xs tw-text-success-100">
                      {t('translation.translationApplied', {
                        defaultValue: 'Translation applied',
                      })}
                    </span>
                    <Button
                      size="small"
                      variant="ghost"
                      onClick={() => onRevert('postText')}
                      isLoading={
                        applyingFields?.postText || applyingFields?.all
                      }
                      disabled={isLocked || isApplying}
                      data-cy="translation-revert-post-text"
                    >
                      {t('translation.useOriginal', {
                        defaultValue: 'Use Original',
                      })}
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="small"
                    onClick={() => onApply('postText')}
                    isLoading={applyingFields?.postText || applyingFields?.all}
                    disabled={isLocked || isApplying}
                    data-cy="translation-apply-post-text"
                  >
                    {t('translation.applyTranslation', {
                      defaultValue: 'Apply Translation',
                    })}
                  </Button>
                ))}
            </div>
            <div className="tw-text-sm tw-text-grayscale-140">
              <HyperText options={{ pageType }}>
                {activeTab === 'original'
                  ? fields.postText?.original || ''
                  : fields.postText?.translated || ''}
              </HyperText>
            </div>
          </div>
        )}

        {/* First Comment Section */}
        {hasFirstComment && (
          <div
            className="tw-rounded-lg tw-border tw-border-grayscale-20 tw-p-3"
            data-cy="translation-results-first-comment"
          >
            <div className="tw-mb-2 tw-flex tw-items-center tw-justify-between">
              <h4 className="tw-text-sm tw-font-medium tw-text-grayscale-180">
                {t('translation.firstCommentSection', {
                  defaultValue: 'First Comment',
                })}
              </h4>
              {canEditText &&
                (appliedFields.firstComment ? (
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <span className="tw-text-xs tw-text-success-100">
                      {t('translation.translationApplied', {
                        defaultValue: 'Translation applied',
                      })}
                    </span>
                    <Button
                      size="small"
                      variant="ghost"
                      onClick={() => onRevert('firstComment')}
                      isLoading={
                        applyingFields?.firstComment || applyingFields?.all
                      }
                      disabled={isLocked || isApplying}
                      data-cy="translation-revert-first-comment"
                    >
                      {t('translation.useOriginal', {
                        defaultValue: 'Use Original',
                      })}
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="small"
                    onClick={() => onApply('firstComment')}
                    isLoading={
                      applyingFields?.firstComment || applyingFields?.all
                    }
                    disabled={isLocked || isApplying}
                    data-cy="translation-apply-first-comment"
                  >
                    {t('translation.applyTranslation', {
                      defaultValue: 'Apply Translation',
                    })}
                  </Button>
                ))}
            </div>
            <div className="tw-text-sm tw-text-grayscale-140">
              <HyperText options={{ pageType }}>
                {activeTab === 'original'
                  ? fields.firstComment?.original || ''
                  : fields.firstComment?.translated || ''}
              </HyperText>
            </div>
          </div>
        )}

        {/* Images Section */}
        {hasImages && (
          <div
            className="tw-rounded-lg tw-border tw-border-grayscale-20 tw-p-3"
            data-cy="translation-results-images"
          >
            <div className="tw-mb-2 tw-flex tw-items-center tw-justify-between">
              <h4 className="tw-text-sm tw-font-medium tw-text-grayscale-180">
                {t('translation.imagesSection', {
                  defaultValue: 'Images',
                })}{' '}
                ({completedImages.length})
              </h4>
              {canEditAssets &&
                (appliedFields.images ? (
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <span className="tw-text-xs tw-text-success-100">
                      {t('translation.translationApplied', {
                        defaultValue: 'Translation applied',
                      })}
                    </span>
                    <Button
                      size="small"
                      variant="ghost"
                      onClick={() => onRevert('images')}
                      isLoading={applyingFields?.images || applyingFields?.all}
                      disabled={isLocked || isApplying}
                      data-cy="translation-revert-images"
                    >
                      {t('translation.useOriginal', {
                        defaultValue: 'Use Original',
                      })}
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="small"
                    onClick={() => onApply('images')}
                    isLoading={applyingFields?.images || applyingFields?.all}
                    disabled={isLocked || isApplying}
                    data-cy="translation-apply-images"
                  >
                    {t('translation.applyTranslation', {
                      defaultValue: 'Apply Translation',
                    })}
                  </Button>
                ))}
            </div>
            <div className="tw-flex tw-flex-wrap tw-gap-2">
              {completedImages.map((img, index) => (
                <MediaPreview.Root key={index}>
                  <MediaPreview.Image
                    src={
                      activeTab === 'original'
                        ? img.originalUrl
                        : img.translatedUrl!
                    }
                  />
                  <MediaPreview.Overlay />
                  <MediaPreview.Actions>
                    <MediaPreview.ActionButton
                      actionType="preview"
                      onClick={() => lightbox.current?.openGallery(index)}
                    />
                    <MediaPreview.ActionButton
                      actionType="download"
                      onClick={() =>
                        window.open(
                          activeTab === 'original'
                            ? img.originalUrl
                            : img.translatedUrl!,
                          '_blank',
                        )
                      }
                    />
                    {activeTab === 'translated' && canEditAssets && page && (
                      <MediaPreview.ActionButton
                        actionType="edit"
                        onClick={() => {
                          setEditingImageUrl(img.translatedUrl!);
                          setEditingCardIndex(img.cardIndex ?? index);
                        }}
                        data-cy={`translation-edit-in-ai-${index}`}
                      />
                    )}
                  </MediaPreview.Actions>
                </MediaPreview.Root>
              ))}
              {failedImages.map((img, index) => (
                <Tooltip
                  key={`failed-${index}`}
                  content={t('translation.imageFailedTooltip', {
                    defaultValue:
                      'Image translation failed. The AI model was unable to translate this image, possibly due to content policy filters or an unsupported image format.',
                  })}
                >
                  <MediaPreview.Root className="tw-border-2 tw-border-danger-100">
                    <MediaPreview.Image src={img.originalUrl} />
                    <MediaPreview.Overlay />
                    <MediaPreview.Actions>
                      <MediaPreview.ActionButton
                        actionType="preview"
                        onClick={() =>
                          lightbox.current?.openGallery(
                            completedImages.length + index,
                          )
                        }
                      />
                    </MediaPreview.Actions>
                  </MediaPreview.Root>
                </Tooltip>
              ))}
            </div>
            <LightBox
              dynamic
              dynamicEl={galleryItems}
              galleryId={`translation-images-${activeTab}`}
              onInit={({ instance }) => (lightbox.current = instance)}
            />
          </div>
        )}
      </div>

      {/* Bulk Action Buttons */}
      <div className="tw-mt-4 tw-flex tw-items-center tw-justify-between">
        <div className="tw-flex tw-items-center tw-gap-2">
          {retranslateSlot}
          {canTriggerTranslation &&
            failedImages.length > 0 &&
            onRetryFailedImages && (
              <Button
                size="small"
                variant="ghost"
                onClick={onRetryFailedImages}
                isLoading={isRetryingImages}
                disabled={isLocked || isApplying || isRetryingImages}
                data-cy="translation-retry-failed-images"
              >
                {t('translation.retryFailedImages', {
                  defaultValue: 'Retry failed ({{count}})',
                  count: failedImages.length,
                })}
              </Button>
            )}
        </div>
        <div className="tw-flex tw-gap-2">
          {canRevertAll && (
            <Button
              size="small"
              variant="ghost"
              onClick={onRevertAll}
              isLoading={applyingFields?.all}
              disabled={isLocked || isApplying}
              data-cy="translation-revert-all"
            >
              {t('translation.useOriginalAll', {
                defaultValue: 'Use Original All',
              })}
            </Button>
          )}
          {canApplyAll && (
            <Button
              size="small"
              onClick={onApplyAll}
              isLoading={applyingFields?.all}
              disabled={isLocked || isApplying}
              data-cy="translation-apply-all"
            >
              {t('translation.applyAllTranslations', {
                defaultValue: 'Apply All Translations',
              })}
            </Button>
          )}
        </div>
      </div>

      {page && (
        <Modal
          open={!!editingImageUrl}
          onClose={() => setEditingImageUrl(null)}
          size="md"
          isContentHeightAutoResizable={false}
          classNames={{
            overlay: '!tw-bg-grayscale-190/90',
            modal:
              '!tw-bg-gradient-to-t !tw-from-purple-160/10 !tw-to-grayscale-10 !tw-rounded-2xl',
          }}
          data-cy="translation-edit-in-ai-modal"
        >
          <TextEditorAiContentProvider
            accountPlanType={accountPlanType}
            disabled={false}
          >
            {editingImageUrl && (
              <AiImageSuggestionsSettings
                page={page}
                mode="edit"
                initialReferenceImages={[editingImageUrl]}
                onSubmitUpload={async (files) => {
                  try {
                    const file = files[0];
                    if (!file) return;

                    await TranslationApi.replaceTranslationImage({
                      postId,
                      cardIndex: editingCardIndex ?? 0,
                      file,
                    });

                    queryClient.invalidateQueries(queryKey.postDetail(postId));
                    queryClient.invalidateQueries(
                      queryKey.postTranslation(postId),
                    );
                    toast(
                      t('translation.imageUpdated', {
                        defaultValue: 'Image updated successfully',
                      }),
                      'success',
                    );
                  } catch {
                    toast(
                      t('translation.imageUpdateFailed', {
                        defaultValue: 'Failed to update image',
                      }),
                      'error',
                    );
                  }
                  setEditingImageUrl(null);
                  setEditingCardIndex(null);
                }}
              />
            )}
          </TextEditorAiContentProvider>
        </Modal>
      )}
    </div>
  );
};

export default TranslationResults;
